import { Loader } from 'components';
import { format } from 'date-fns';
import React, {
  createContext,
  ReactNode,
  useContext,
  useState,
  useEffect,
} from 'react';
import { UserSearch } from 'types/UserSearch';

// ---------- Helpers ----------
const initialDateSetup = () => {
  const currentDate = new Date();
  if (currentDate.getMinutes() < 30) {
    currentDate.setMinutes(30);
  } else {
    currentDate.setHours(currentDate.getHours() + 1);
    currentDate.setMinutes(0);
  }
  currentDate.setSeconds(0);
  return currentDate;
};

const initialDate = initialDateSetup();
export const initialUserSearchState: UserSearch = {
  OutDetails: {
    BranchId: '',
    Date: format(initialDate, 'dd MMM yyyy'),
    StateCode: '',
    IsDelOrCol: false,
    Time: format(initialDate, 'h:mm a'),
    BranchName: '',
    StateName: '',
    BranchCode: '',
  },
  InDetails: {
    BranchId: '',
    Date: format(initialDate, 'dd MMM yyyy'),
    StateCode: '',
    IsDelOrCol: false,
    Time: format(initialDate, 'h:mm a'),
    BranchName: '',
    StateName: '',
    BranchCode: '',
  },
  Daily: true,
  CountryCode: 'UAE',
  DeviceInfo: 'Kiosk',
  Brand: 'Thrifty',

  IPLocation: {
    CountryCode: 'AE',
    CountryName: 'United Arab Emirates',
    IPAddress: 'http://127.0.0.1',
    City: 'Dubai',
    State: 'Dubai',
  },
  Renter: {
    Id: '',
    FirstName: '',
    LastName: '',
    EmailId: '',
    ContactNo: null,
    Nationality: '',
    VisaStatus: 'Visitor',
    IsSignUp: false,
    IsAbove21: false,
    IsTCAccepted: false,
  },
  RentalDays: 0,
  isPickupField: false,
  Query: '',
  insuranceChargeCode: ['WTP', 'SCDW', 'CDW', 'PAI', 'SDW', 'FCO'],
  ancilarriesChargeCode: ['COT', 'VRF', 'ADR'],
  chargeCodes: ['PPD'],
  paymentStatus: 1,
  VehicleModel: {
    VehicleId: '',
    Name: '',
    Code: '',
    ModelYear: 0,
    Engine: '',
    Passengers: 0,
    AutoTransmission: false,
    Doors: 0,
    FuelType: '',
    VehicleImage: '',
    UpgradeDiscount: 0,
  },
  RentalRate: [],
  ExtrasRentalRate: [],
  PrepaidDiscount: 0,
  InsuranceTotal: 0,
  AncillaryTotal: 0,
  SubTotal: 0,
  TotalTax: 0,
  TotalAmount: 0,
  PaymentStatus: 0,
  PaymentMethod: 0,
  PaidDuration: 0,
  SecurityDeposit: 0,
  branchType: '',
  activeBranchId: '',
  PromoCode: '',
  PromoCodeDis: 0,
};

// ---------- Context Types ----------
interface UserSearchContextProps {
  bookingSearchData: UserSearch;
  setBookingSearchData: React.Dispatch<React.SetStateAction<UserSearch>>;
  loading: boolean;
}

// ---------- Context ----------
export const UserSearchContext = createContext<
  UserSearchContextProps | undefined
>(undefined);

// ---------- Provider ----------
interface UserSearchContextProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'bookingSearchData';

export const UserSearchContextProvider: React.FC<
  UserSearchContextProviderProps
> = ({ children }) => {
  const [bookingSearchData, setBookingSearchData] = useState<UserSearch>(() => {
    // Load from localStorage if available
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : initialUserSearchState;
  });

  const [loading, setLoading] = useState(true);

  // Persist to localStorage whenever bookingSearchData changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bookingSearchData));
  }, [bookingSearchData]);

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <UserSearchContext.Provider
      value={{ bookingSearchData, setBookingSearchData, loading }}
    >
      {children}
    </UserSearchContext.Provider>
  );
};

// ---------- Hook ----------
export const useUserSearchContext = () => {
  const ctx = useContext(UserSearchContext);
  if (!ctx) {
    throw new Error(
      'useUserSearchContext must be used within UserSearchContextProvider'
    );
  }
  return ctx;
};
